import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    forwardRef,
} from "react";
import { Loader2, Plus, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { crud } from "../lib/api";
import SearchableSelect from "./SearchableSelect";

let queueIdSeq = 0;
const nextQueueId = () => `q-${++queueIdSeq}`;

const blankDraft = () => ({
    masraf_turu: null,
    masraf_turu_label: "",
    miktar: "",
});

const INPUT_CLS =
    "w-full h-10 bg-[#0B0F19] border border-white/10 rounded-md px-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition";

function round2(n) {
    return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

/** Backend OdemeTakibi.hesapla_hakedis ile aynı mantık */
export function computeOdemeHakedis(odemeMiktari, anlasmaOrani, toplamMasraf) {
    const odeme = Number(odemeMiktari) || 0;
    const oran = (Number(anlasmaOrani) || 0) / 100;
    const masraf = Number(toplamMasraf) || 0;
    const anlasmaPay = odeme * oran;
    return {
        toplam_masraf: round2(masraf),
        sigortali_hakedis: round2(odeme - anlasmaPay - masraf),
        mbs_hakedis: round2(anlasmaPay + masraf),
    };
}

function money(value) {
    const n = Number(value || 0);
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0);
}

function SummaryRow({ label, value, accent }) {
    return (
        <div className="flex flex-col gap-1 min-w-0 py-1">
            <span className="text-[10px] uppercase tracking-[0.12em] text-slate-500 leading-tight">
                {label}
            </span>
            <span
                className={
                    "text-sm font-semibold tabular-nums leading-snug truncate " +
                    (accent ? "text-[#F3E5AB]" : "text-slate-100")
                }
                title={money(value)}
            >
                {money(value)}
            </span>
        </div>
    );
}

function FieldLabel({ children }) {
    return (
        <span className="block text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1.5">
            {children}
        </span>
    );
}

const OdemeMasrafSection = forwardRef(function OdemeMasrafSection(
    { parentId, previewOdeme = null, onTotalsChange, embedded = false },
    ref,
) {
    const [loading, setLoading] = useState(!!parentId);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState([]);
    const [pending, setPending] = useState([]);
    const [odeme, setOdeme] = useState(null);
    const [draft, setDraft] = useState(blankDraft());
    const pendingRef = useRef(pending);

    useEffect(() => {
        pendingRef.current = pending;
    }, [pending]);

    const formQueueMode = embedded && !parentId;
    const queueMode = !parentId && !formQueueMode;
    const showMasrafUi = Boolean(parentId) || formQueueMode;

    const load = useCallback(async () => {
        if (!parentId) {
            setRows([]);
            setOdeme(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const [masrafData, odemeData] = await Promise.all([
                crud.list("masraf-detaylari", {
                    odeme_takibi: parentId,
                    page_size: 200,
                }),
                crud.retrieve("odemeler", parentId),
            ]);
            const list = Array.isArray(masrafData)
                ? masrafData
                : masrafData?.results || [];
            setRows(
                list.filter((r) => String(r?.odeme_takibi) === String(parentId)),
            );
            setOdeme(odemeData || null);
        } catch {
            setRows([]);
            setOdeme(null);
        } finally {
            setLoading(false);
        }
    }, [parentId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!formQueueMode) setPending([]);
    }, [formQueueMode, parentId]);

    const displayRows = useMemo(() => {
        if (formQueueMode) return pending;
        return rows;
    }, [formQueueMode, pending, rows]);

    const toplamMasraf = useMemo(
        () =>
            displayRows.reduce((sum, r) => sum + (Number(r.miktar) || 0), 0),
        [displayRows],
    );

    const summary = useMemo(() => {
        const odemeMiktari =
            previewOdeme?.odeme_miktari ?? odeme?.odeme_miktari ?? 0;
        const anlasmaOrani =
            previewOdeme?.anlasma_orani ?? odeme?.anlasma_orani ?? 0;
        return computeOdemeHakedis(odemeMiktari, anlasmaOrani, toplamMasraf);
    }, [previewOdeme, odeme, toplamMasraf]);

    useEffect(() => {
        if (!onTotalsChange) return;
        onTotalsChange(summary);
    }, [summary, onTotalsChange]);

    const canAdd = useMemo(() => {
        const amount = Number(draft.miktar);
        return Boolean(draft.masraf_turu) && Number.isFinite(amount) && amount > 0;
    }, [draft]);

    useImperativeHandle(
        ref,
        () => ({
            hasQueue: () => pendingRef.current.length > 0,
            flushQueue: async (newParentId) => {
                if (!newParentId) return { success: 0, failed: 0 };
                const items = [...pendingRef.current];
                let success = 0;
                let failed = 0;
                for (const item of items) {
                    try {
                        await crud.create("masraf-detaylari", {
                            odeme_takibi: newParentId,
                            masraf_turu: item.masraf_turu,
                            miktar: Number(item.miktar),
                        });
                        success += 1;
                    } catch {
                        failed += 1;
                    }
                }
                if (success > 0) setPending([]);
                return { success, failed };
            },
        }),
        [],
    );

    function addPendingRow() {
        setPending((arr) => [
            ...arr,
            {
                id: nextQueueId(),
                masraf_turu: draft.masraf_turu,
                masraf_turu_label: draft.masraf_turu_label || "Masraf",
                miktar: Number(draft.miktar),
            },
        ]);
        setDraft(blankDraft());
        toast.success("Masraf eklendi (kayıtla birlikte kaydedilecek).");
    }

    async function addRow() {
        if (!canAdd) {
            toast.error("Masraf türü ve geçerli tutar girin.");
            return;
        }
        if (formQueueMode) {
            addPendingRow();
            return;
        }
        if (!parentId) {
            toast.info("Önce ödeme kaydını kaydedin.");
            return;
        }
        setSaving(true);
        try {
            await crud.create("masraf-detaylari", {
                odeme_takibi: parentId,
                masraf_turu: draft.masraf_turu,
                miktar: Number(draft.miktar),
            });
            setDraft(blankDraft());
            toast.success("Masraf kalemi eklendi.");
            await load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || e?.message || "Masraf eklenemedi.",
            );
        } finally {
            setSaving(false);
        }
    }

    function removePendingRow(id) {
        setPending((arr) => arr.filter((r) => r.id !== id));
    }

    async function deleteRow(id) {
        if (String(id).startsWith("q-")) {
            removePendingRow(id);
            return;
        }
        if (!window.confirm("Bu masraf kalemi silinsin mi?")) return;
        try {
            await crud.remove("masraf-detaylari", id);
            toast.success("Masraf kalemi silindi.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Silinemedi.");
        }
    }

    const odemeMiktari =
        previewOdeme?.odeme_miktari ?? odeme?.odeme_miktari ?? 0;

    const shellCls = embedded
        ? "md:col-span-2 rounded-lg border border-white/10 bg-[#0B0F19]/50 overflow-visible"
        : "md:col-span-2 mt-2 rounded-lg border border-[var(--mbs-border)] bg-[var(--mbs-surface)] overflow-visible shadow-sm";

    return (
        <fieldset className={shellCls}>
            <legend className="px-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">
                Masraflar ve hakediş
            </legend>

            <div className="px-4 pb-4 pt-1 space-y-5">
                {queueMode ? (
                    <p className="text-xs text-amber-200/90 border border-amber-500/25 rounded-md px-3 py-2.5 bg-amber-500/5">
                        Önce ödemeyi kaydedin; ardından masraf kalemlerini
                        ekleyebilirsiniz.
                    </p>
                ) : null}

                {showMasrafUi ? (
                    <>
                        {formQueueMode ? (
                            <p className="text-xs text-slate-400">
                                Masraf kalemleri ödeme ile birlikte kaydedilir.
                            </p>
                        ) : null}

                        {/* Hesap özeti — tek satır, okunaklı */}
                        <div className="rounded-md border border-white/10 bg-[#121A2F]/80 p-4">
                            <div className="flex items-center justify-between gap-2 mb-3">
                                <span className="text-[11px] font-medium text-slate-300">
                                    Hesap özeti
                                </span>
                                <span className="text-[10px] text-slate-500">
                                    Ödeme ve anlaşma oranına göre otomatik
                                </span>
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4">
                                <SummaryRow
                                    label="Ödeme miktarı"
                                    value={odemeMiktari}
                                />
                                <SummaryRow
                                    label="Toplam masraf"
                                    value={summary.toplam_masraf}
                                    accent
                                />
                                <SummaryRow
                                    label="Sigortalı hakediş"
                                    value={summary.sigortali_hakedis}
                                />
                                <SummaryRow
                                    label="MBS hakediş"
                                    value={summary.mbs_hakedis}
                                    accent
                                />
                            </div>
                        </div>

                        {/* Masraf listesi */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[11px] font-medium text-slate-300">
                                    Masraf kalemleri
                                </span>
                                <span className="text-[10px] text-slate-500">
                                    {displayRows.length} kalem
                                </span>
                            </div>

                            {loading && parentId ? (
                                <div className="py-8 flex items-center justify-center gap-2 text-sm text-slate-500">
                                    <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" />
                                    Yükleniyor…
                                </div>
                            ) : displayRows.length === 0 ? (
                                <p className="text-sm text-slate-500 py-3 px-3 rounded-md border border-dashed border-white/10 text-center">
                                    Henüz masraf yok. Aşağıdan ekleyin.
                                </p>
                            ) : (
                                <div className="rounded-md border border-white/10 overflow-hidden divide-y divide-white/10">
                                    {displayRows.map((r) => (
                                        <div
                                            key={r.id}
                                            className="flex items-center gap-3 px-3 py-2.5 bg-[#0B0F19]/40 hover:bg-white/[0.03]"
                                        >
                                            <Receipt className="h-4 w-4 text-slate-500 shrink-0" />
                                            <span className="flex-1 min-w-0 text-sm text-slate-200 truncate">
                                                {r.masraf_turu_label ||
                                                    `Masraf #${r.masraf_turu}`}
                                            </span>
                                            <span className="text-sm font-semibold tabular-nums text-[#F3E5AB] shrink-0">
                                                {money(r.miktar)}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => deleteRow(r.id)}
                                                className="h-8 w-8 shrink-0 rounded-md flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition"
                                                title="Kalemi sil"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Yeni kalem */}
                        <div className="rounded-md border border-white/10 bg-[#121A2F]/50 p-4">
                            <p className="text-[11px] font-medium text-slate-300 mb-3">
                                Yeni masraf kalemi
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-3 items-end">
                                <div className="min-w-0">
                                    <FieldLabel>Masraf türü</FieldLabel>
                                    <SearchableSelect
                                        value={draft.masraf_turu}
                                        onChange={(id, meta) =>
                                            setDraft((d) => ({
                                                ...d,
                                                masraf_turu: id,
                                                masraf_turu_label:
                                                    meta?.label || "",
                                            }))
                                        }
                                        resource="masraf-turleri"
                                        placeholder="Seçin…"
                                        testId="odeme-masraf-turu"
                                        dropdownDirection="up"
                                    />
                                </div>
                                <div>
                                    <FieldLabel>Tutar (TL)</FieldLabel>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={draft.miktar}
                                        onChange={(e) =>
                                            setDraft((d) => ({
                                                ...d,
                                                miktar: e.target.value,
                                            }))
                                        }
                                        className={INPUT_CLS}
                                        placeholder="0,00"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={addRow}
                                    disabled={saving || !canAdd}
                                    className="btn-gold h-10 px-4 rounded-md text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap"
                                >
                                    {saving ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Plus className="h-4 w-4" />
                                    )}
                                    Ekle
                                </button>
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </fieldset>
    );
});

export default OdemeMasrafSection;
